import type { HTMLAttributes } from "react";
import AltArrowDownLinear from "~icons/solar/alt-arrow-down-linear";
import { STATUS_STYLE_MAP } from "../../types/candidate";
import type { StatusColor } from "../../types/candidate";
import { cn } from "../../lib/utils";

export type StatusTone = StatusColor;

/**
 * Display label → tone. One map for project, candidate, meeting, client and
 * talent-pool states. Unknown labels fall back to slate.
 */
const TONE_BY_LABEL: Record<string, StatusTone> = {
  // neutral
  uncontacted: "grey",
  cancelled: "grey",
  withdrawn: "grey",
  // in motion
  contacted: "orange",
  vetting: "orange",
  screening: "orange",
  "in progress": "orange",
  upcoming: "orange",
  active: "orange",
  // brand
  vetted: "pink",
  shortlisted: "pink",
  interview: "pink",
  interviewing: "pink",
  interviewed: "pink",
  // confirmed
  verified: "blue",
  public: "blue",
  hired: "blue",
  // done
  finished: "green",
  completed: "green",
  offered: "green",
  resolved: "green",
  // stopped
  rejected: "red",
  "on hold": "red",
  // dormant
  expired: "slate",
  draft: "slate",
  inactive: "slate",
  closed: "slate",
};

// eslint-disable-next-line react-refresh/only-export-components
export function toneForStatus(label: string): StatusTone {
  return TONE_BY_LABEL[label.trim().toLowerCase()] ?? "slate";
}

// eslint-disable-next-line react-refresh/only-export-components
export function statusPillClasses(tone: StatusTone, className?: string) {
  const s = STATUS_STYLE_MAP[tone];
  return cn(
    "inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-2.5 py-0.5 text-xs font-semibold leading-5",
    s.bg,
    s.text,
    s.border,
    className
  );
}

export interface StatusPillProps extends HTMLAttributes<HTMLSpanElement> {
  status: string;
  /** Override the label→tone lookup. */
  tone?: StatusTone;
  /** Render a chevron; pair with an onClick or wrap in a Dropdown trigger. */
  interactive?: boolean;
  size?: "sm" | "md";
}

export function StatusPill({
  status,
  tone,
  interactive = false,
  size = "md",
  className,
  ...props
}: StatusPillProps) {
  const resolved = tone ?? toneForStatus(status);
  return (
    <span
      className={cn(
        statusPillClasses(resolved),
        size === "sm" && "px-2 text-[11px] leading-4",
        interactive && "cursor-pointer pr-2 transition-opacity hover:opacity-80",
        className
      )}
      {...props}
    >
      {status}
      {interactive && (
        <AltArrowDownLinear aria-hidden="true" className="size-3.5" />
      )}
    </span>
  );
}
